import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Html5Qrcode } from "html5-qrcode";
import {
  Wrench, ScanLine, Truck, PackagePlus, Camera, CameraOff, LogIn, LogOut, Gauge, Plus, X, AlertTriangle,
} from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { Eyebrow, Spinner, StatusPill } from "@/components/tac";

const TOOL_STATUS = { in_van: "active", checked_out: "in_progress", maintenance: "needs_review" };

function Scanner({ onDetected }) {
  const [on, setOn] = useState(false);
  const [manual, setManual] = useState("");
  const ref = useRef(null);
  const scannerRef = useRef(null);

  useEffect(() => {
    return () => { if (scannerRef.current) scannerRef.current.stop().catch(() => {}); };
  }, []);

  const start = async () => {
    setOn(true);
    try {
      const s = new Html5Qrcode("qr-reader");
      scannerRef.current = s;
      await s.start({ facingMode: "environment" }, { fps: 10, qrbox: 220 },
        (text) => { onDetected(text); }, () => {});
    } catch (e) {
      toast.error("Camera unavailable - use manual entry");
      setOn(false);
    }
  };
  const stop = async () => { if (scannerRef.current) await scannerRef.current.stop().catch(() => {}); setOn(false); };

  return (
    <div className="card-tac p-5" data-testid="tool-scanner">
      <div className="mb-4 flex items-center gap-2"><ScanLine className="h-4 w-4 text-cyan-400" /><h3 className="font-display font-semibold text-white">Scan barcode</h3></div>
      <div id="qr-reader" ref={ref} className={`overflow-hidden rounded-xl ${on ? "mb-3 border border-slate-700" : "hidden"}`} />
      <div className="flex gap-2">
        {!on ? (
          <button data-testid="scanner-start-btn" onClick={start} className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2 text-sm text-white hover:border-cyan-500"><Camera className="h-4 w-4" /> Start camera</button>
        ) : (
          <button data-testid="scanner-stop-btn" onClick={stop} className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2 text-sm text-white hover:border-rose-500"><CameraOff className="h-4 w-4" /> Stop</button>
        )}
      </div>
      <div className="mt-3 flex gap-2">
        <input data-testid="manual-barcode-input" value={manual} onChange={(e) => setManual(e.target.value)} placeholder="Or type barcode (e.g. TL-1001)"
          className="flex-1 rounded-lg border border-slate-700 bg-[#0d1420] px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none" />
        <button data-testid="manual-barcode-submit" onClick={() => manual && onDetected(manual)} className="btn-amber rounded-lg px-4 py-2 text-sm">Use</button>
      </div>
    </div>
  );
}

export default function Fleet() {
  const qc = useQueryClient();
  const [scanned, setScanned] = useState("");
  const [action, setAction] = useState("check_out");
  const [newTool, setNewTool] = useState(false);
  const [tool, setTool] = useState({ name: "", barcode: "", category: "hand_tool" });
  const [restockOpen, setRestockOpen] = useState(false);
  const [restock, setRestock] = useState({ van_id: "", vendor_qbo_id: "", items: [{ part: "", qty: 1, unit_price: 25 }] });
  const [miles, setMiles] = useState({});

  const { data: tools, isLoading } = useQuery({ queryKey: ["tools"], queryFn: async () => (await api.get("/tools")).data });
  const { data: vehicles } = useQuery({ queryKey: ["vehicles"], queryFn: async () => (await api.get("/vehicles")).data });
  const { data: txns } = useQuery({ queryKey: ["tool-txns"], queryFn: async () => (await api.get("/tools/transactions")).data });
  const { data: manifests } = useQuery({ queryKey: ["restock"], queryFn: async () => (await api.get("/restock")).data });
  const { data: qboRefs } = useQuery({ queryKey: ["qbo-refs"], queryFn: async () => (await api.get("/qbo/refs")).data });

  const doScan = async (barcode) => {
    setScanned(barcode);
    try {
      const { data } = await api.post("/tools/scan", { barcode, action });
      toast.success(`${data.tool.name} → ${data.tool.status.replace(/_/g, " ")}`);
      qc.invalidateQueries({ queryKey: ["tools"] }); qc.invalidateQueries({ queryKey: ["tool-txns"] });
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const addTool = async () => {
    try { await api.post("/tools", tool); toast.success("Tool added"); setNewTool(false); setTool({ name: "", barcode: "", category: "hand_tool" }); qc.invalidateQueries({ queryKey: ["tools"] }); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const logTrip = async (vehicle_id) => {
    const m = Number(miles[vehicle_id] || 0);
    if (!m) return toast.error("Enter miles");
    try { const { data } = await api.post("/vehicles/trip", { vehicle_id, miles: m }); toast.success(`Odometer ${data.odometer} mi${data.maintenance_due ? " · maintenance due!" : ""}`); setMiles({ ...miles, [vehicle_id]: "" }); qc.invalidateQueries({ queryKey: ["vehicles"] }); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const serviceVehicle = async (vehicle_id) => {
    try { const { data } = await api.post(`/vehicles/${vehicle_id}/service`); toast.success(`Serviced · next due at ${data.maintenance_due_miles} mi`); qc.invalidateQueries({ queryKey: ["vehicles"] }); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const vanName = (id) => (vehicles || []).find((v) => v.id === id)?.name || (id || "").slice(0, 8);
  const syncPO = async (manifest_id) => {
    try { const { data } = await api.post("/qbo/sync/po", { manifest_id }); toast.success(`PO synced to QuickBooks · #${data.qbo_po_id}`); qc.invalidateQueries({ queryKey: ["restock"] }); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const submitRestock = async () => {
    try { await api.post("/restock", restock); toast.success("Restock manifest created"); setRestockOpen(false); setRestock({ van_id: "", vendor_qbo_id: "", items: [{ part: "", qty: 1, unit_price: 25 }] }); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div className="mx-auto max-w-[1400px] p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><Eyebrow>Fleet · Tools · Inventory</Eyebrow><h1 className="mt-2 font-display text-3xl font-bold text-white">Asset control</h1></div>
        <div className="flex gap-2">
          <button data-testid="new-tool-btn" onClick={() => setNewTool(true)} className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2.5 text-sm text-white hover:border-amber-500"><Plus className="h-4 w-4" /> Tool</button>
          <button data-testid="new-restock-btn" onClick={() => setRestockOpen(true)} className="btn-amber inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm"><PackagePlus className="h-4 w-4" /> Restock manifest</button>
        </div>
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-12">
        <div className="lg:col-span-5 space-y-6">
          <Scanner onDetected={doScan} />
          <div className="card-tac p-5">
            <div className="mb-3 font-mono-tac text-[11px] uppercase tracking-widest text-slate-500">Scan action</div>
            <div className="flex gap-2">
              {[["check_out", "Check out", LogOut], ["check_in", "Check in", LogIn]].map(([val, label, Icon]) => (
                <button key={val} data-testid={`scan-action-${val}`} onClick={() => setAction(val)}
                  className={`inline-flex flex-1 items-center justify-center gap-2 rounded-xl border py-2.5 text-sm ${action === val ? "border-amber-500 bg-amber-500/10 text-amber-300" : "border-slate-700 text-slate-300"}`}>
                  <Icon className="h-4 w-4" /> {label}
                </button>
              ))}
            </div>
            {scanned && <p className="mt-3 font-mono-tac text-xs text-cyan-400">Last scan: {scanned}</p>}
          </div>
        </div>

        <div className="lg:col-span-7 space-y-6">
          <div className="card-tac">
            <div className="border-b border-slate-800 px-5 py-4 font-display font-semibold text-white flex items-center gap-2"><Wrench className="h-4 w-4 text-cyan-400" /> Tools</div>
            {isLoading ? <div className="p-6"><Spinner /></div> : (
              <div className="divide-y divide-slate-800">
                {tools.map((t) => (
                  <div key={t.id} className="flex items-center justify-between px-5 py-3" data-testid={`tool-row-${t.barcode}`}>
                    <div><div className="text-sm text-white">{t.name}</div><div className="font-mono-tac text-xs text-slate-500">{t.barcode} · {t.category}</div></div>
                    <StatusPill status={TOOL_STATUS[t.status] || "none"} />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="card-tac">
            <div className="border-b border-slate-800 px-5 py-4 font-display font-semibold text-white flex items-center gap-2"><Truck className="h-4 w-4 text-cyan-400" /> Vehicles & mileage</div>
            <div className="divide-y divide-slate-800">
              {(vehicles || []).map((v) => {
                const due = v.odometer >= (v.maintenance_due_miles ?? Infinity);
                return (
                <div key={v.id} className="flex items-center justify-between px-5 py-3" data-testid={`vehicle-row-${v.id}`}>
                  <div>
                    <div className="text-sm text-white">{v.name}</div>
                    <div className="font-mono-tac text-xs text-slate-500 flex items-center gap-1"><Gauge className="h-3 w-3" /> {v.odometer} mi · due {v.maintenance_due_miles} mi</div>
                    {due && <span data-testid={`maint-due-${v.id}`} className="mt-1 inline-flex items-center gap-1 rounded-full border border-rose-500/40 bg-rose-950/40 px-2 py-0.5 font-mono-tac text-[10px] text-rose-300"><AlertTriangle className="h-3 w-3" /> maintenance due</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <input data-testid={`trip-miles-${v.id}`} type="number" placeholder="miles" value={miles[v.id] || ""} onChange={(e) => setMiles({ ...miles, [v.id]: e.target.value })}
                      className="w-20 rounded-lg border border-slate-700 bg-[#0d1420] px-2 py-1.5 text-sm text-white focus:border-amber-500 focus:outline-none" />
                    <button data-testid={`log-trip-${v.id}`} onClick={() => logTrip(v.id)} className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs text-white hover:border-amber-500">Log trip</button>
                    <button data-testid={`service-${v.id}`} onClick={() => serviceVehicle(v.id)} className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs text-white hover:border-emerald-500">Mark serviced</button>
                  </div>
                </div>
              );})}
            </div>
          </div>

          <div className="card-tac">
            <div className="border-b border-slate-800 px-5 py-4 font-display font-semibold text-white">Recent tool activity</div>
            <div className="divide-y divide-slate-800 max-h-64 overflow-y-auto">
              {(txns || []).map((t) => (
                <div key={t.id} className="flex items-center justify-between px-5 py-2.5 text-sm" data-testid={`txn-row-${t.id}`}>
                  <span className="text-slate-300">{t.tool_name} · {t.action.replace(/_/g, " ")}</span>
                  <span className="font-mono-tac text-xs text-slate-600">{(t.at || "").slice(11, 19)}</span>
                </div>
              ))}
              {!(txns || []).length && <div className="px-5 py-6 text-center text-sm text-slate-500">No activity yet - scan a tool.</div>}
            </div>
          </div>

          <div className="card-tac">
            <div className="border-b border-slate-800 px-5 py-4 font-display font-semibold text-white flex items-center gap-2"><PackagePlus className="h-4 w-4 text-cyan-400" /> Restock manifests → QuickBooks PO</div>
            <div className="divide-y divide-slate-800">
              {(manifests || []).map((m) => (
                <div key={m.id} className="flex items-center justify-between px-5 py-3" data-testid={`manifest-row-${m.id}`}>
                  <div>
                    <div className="text-sm text-white">{vanName(m.van_id)} · {m.items.length} part(s)</div>
                    <div className="font-mono-tac text-xs text-slate-500">{m.qbo_ref || m.status}</div>
                  </div>
                  <button data-testid={`sync-po-${m.id}`} onClick={() => syncPO(m.id)} disabled={m.status === "po_synced"}
                    className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs text-white hover:border-emerald-500 disabled:opacity-50">
                    {m.status === "po_synced" ? "PO synced ✓" : "Sync PO to QBO"}
                  </button>
                </div>
              ))}
              {!(manifests || []).length && <div className="px-5 py-6 text-center text-sm text-slate-500">No manifests yet.</div>}
            </div>
          </div>
        </div>
      </div>

      {newTool && (
        <Modal title="Add tool" onClose={() => setNewTool(false)}>
          <input data-testid="tool-name-input" placeholder="Tool name" value={tool.name} onChange={(e) => setTool({ ...tool, name: e.target.value })} className={inp} />
          <input data-testid="tool-barcode-input" placeholder="Barcode / QR value" value={tool.barcode} onChange={(e) => setTool({ ...tool, barcode: e.target.value })} className={inp} />
          <select data-testid="tool-category-select" value={tool.category} onChange={(e) => setTool({ ...tool, category: e.target.value })} className={inp}>
            <option value="hand_tool">Hand tool</option><option value="power_tool">Power tool</option><option value="test_equipment">Test equipment</option>
          </select>
          <button data-testid="tool-add-submit" disabled={!tool.name || !tool.barcode} onClick={addTool} className="btn-amber w-full rounded-xl py-3 text-sm disabled:opacity-50">Add tool</button>
        </Modal>
      )}
      {restockOpen && (
        <Modal title="Van restock manifest → PO" onClose={() => setRestockOpen(false)}>
          <select data-testid="restock-van-select" value={restock.van_id} onChange={(e) => setRestock({ ...restock, van_id: e.target.value })} className={inp}>
            <option value="">Select van…</option>
            {(vehicles || []).map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          <select data-testid="restock-vendor-select" value={restock.vendor_qbo_id} onChange={(e) => setRestock({ ...restock, vendor_qbo_id: e.target.value })} className={inp}>
            <option value="">{qboRefs?.connected ? "Select QuickBooks vendor…" : "Company default vendor (connect QBO to map)"}</option>
            {(qboRefs?.vendors || []).map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          {restock.items.map((it, i) => (
            <div key={i} className="flex gap-2">
              <input data-testid={`restock-part-${i}`} placeholder="Part" value={it.part} onChange={(e) => setRestock({ ...restock, items: restock.items.map((x, j) => j === i ? { ...x, part: e.target.value } : x) })} className={inp} />
              <input data-testid={`restock-qty-${i}`} type="number" title="qty" value={it.qty} onChange={(e) => setRestock({ ...restock, items: restock.items.map((x, j) => j === i ? { ...x, qty: Number(e.target.value) } : x) })} className="w-16 rounded-xl border border-slate-700 bg-[#0d1420] px-2 text-sm text-white focus:border-amber-500 focus:outline-none" />
              <input data-testid={`restock-price-${i}`} type="number" title="unit price" value={it.unit_price} onChange={(e) => setRestock({ ...restock, items: restock.items.map((x, j) => j === i ? { ...x, unit_price: Number(e.target.value) } : x) })} className="w-20 rounded-xl border border-slate-700 bg-[#0d1420] px-2 text-sm text-white focus:border-amber-500 focus:outline-none" />
            </div>
          ))}
          <button data-testid="restock-add-line" onClick={() => setRestock({ ...restock, items: [...restock.items, { part: "", qty: 1, unit_price: 25 }] })} className="text-xs text-cyan-400">+ Add part</button>
          <button data-testid="restock-submit" disabled={!restock.van_id} onClick={submitRestock} className="btn-amber w-full rounded-xl py-3 text-sm disabled:opacity-50">Create manifest</button>
        </Modal>
      )}
    </div>
  );
}

const inp = "w-full rounded-xl border border-slate-700 bg-[#0d1420] px-4 py-3 text-sm text-white focus:border-amber-500 focus:outline-none";
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="card-tac w-full max-w-md space-y-3 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between"><h2 className="font-display text-lg font-semibold text-white">{title}</h2><button onClick={onClose} className="text-slate-500 hover:text-white"><X className="h-5 w-5" /></button></div>
        {children}
      </div>
    </div>
  );
}
